import * as THREE from 'three';
import { TRANSITION_CONFIG } from '../utils/Constants.js';
import { ImageLoadError } from '../utils/Errors.js';

/**
 * Manages 360° images, their loading, and transitions
 */
export class ImageManager {
    constructor(sphereManager, textureLoader, sceneManager = null, toast = null) {
        this.sphereManager = sphereManager;
        this.textureLoader = textureLoader;
        this.sceneManager = sceneManager;
        this.toast = toast;

        this.images = [];
        this.currentImageIndex = -1;
        this.imageCounter = 0;

        // Picture ID to image data mapping (for API connections)
        this.pictureIdToImageMap = new Map();

        // Hash to image data mapping (for deduplication)
        this.hashToImageMap = new Map();

        // Transition state
        this.isTransitioning = false;
        this.transitionProgress = 0;
        this.transitionDuration = TRANSITION_CONFIG.DEFAULT_DURATION;
        this.transitionStartTime = 0;
        this.currentTexture = null;
        this.nextTexture = null;
        this.transitionSphere = null;
        this.transitionSphereInitialized = false;

        // Camera transition state
        this.cameraStartPosition = null;

        // Callbacks
        this.onImageAdded = null;
        this.onImageChanged = null;
        this.onTransitionStart = null;
        this.onTransitionComplete = null;
        this.onThumbnailNeeded = null; // Callback for thumbnail creation
    }

    /**
     * Calculate SHA-256 hash of a file for deduplication
     * @param {File|Blob} file - File to hash
     * @returns {Promise<string>} Hash string
     */
    async calculateFileHash(file) {
        try {
            const arrayBuffer = await file.arrayBuffer();
            const hashBuffer = await crypto.subtle.digest('SHA-256', arrayBuffer);
            const hashArray = Array.from(new Uint8Array(hashBuffer));
            const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
            return hashHex;
        } catch (error) {
            console.error('[ImageManager] Failed to calculate file hash:', error);
            // Fallback: use filename + size as a weaker identifier
            return `${file.name}_${file.size}`;
        }
    }

    /**
     * Initialize the transition sphere once (avoids lag on first transition)
     * @param {THREE.Scene} scene - Scene to add the sphere to
     */
    initializeTransitionSphere(scene) {
        if (this.transitionSphereInitialized) return;

        console.log('[ImageManager] Pre-creating transition sphere for smooth transitions...');

        // Create transition sphere with simple geometry
        const geometry = new THREE.SphereGeometry(500, 32, 32);
        const material = new THREE.MeshBasicMaterial({
            side: THREE.BackSide,
            transparent: true,
            opacity: 0.0
        });

        this.transitionSphere = new THREE.Mesh(geometry, material);
        this.transitionSphere.visible = false; // Hidden by default
        scene.add(this.transitionSphere);

        this.transitionSphereInitialized = true;
        console.log('[ImageManager] Transition sphere ready');
    }

    /**
     * Load and add a new 360° image
     * @param {File} file - Image file
     * @param {number|null} initialRotation - Initial rotation in degrees
     * @param {boolean} silent - If true, don't apply texture or trigger callbacks (for batch loading)
     * @returns {Promise<Object>} Image data
     * @throws {ImageLoadError} If image fails to load
     */
    async addImage(file, initialRotation = null, silent = false) {
        try {
            // Calculate hash for deduplication
            const hash = await this.calculateFileHash(file);

            // Check if this image already exists
            const existingImageData = this.hashToImageMap.get(hash);
            if (existingImageData) {
                console.warn(`[ImageManager] Image "${file.name}" already exists (duplicate detected). Using existing image (id: ${existingImageData.id}).`);

                // Show toast notification to user
                if (this.toast) {
                    this.toast.warning(`L'image "${file.name}" a déjà été ajoutée.`, 3500);
                }

                return existingImageData;
            }

            // Use existing URL if available (from API cache), otherwise create blob URL
            const url = file.url || URL.createObjectURL(file);
            const texture = await this.textureLoader.load360Texture(url);

        this.imageCounter++;
        const imageData = {
            id: this.imageCounter,
            name: file.name,
            url: url,
            texture: texture,
            initialRotation: initialRotation,
            hash: hash
        };

        this.images.push(imageData);
        this.currentImageIndex = this.images.length - 1;

        // Store hash mapping for deduplication
        this.hashToImageMap.set(hash, imageData);

        // Trigger thumbnail creation immediately (before UI callbacks)
        if (this.onThumbnailNeeded) {
            this.onThumbnailNeeded(imageData);
        }

        if (!silent) {
            // Apply texture to sphere
            this.sphereManager.setTexture(texture);

            // Apply initial rotation if specified (via camera, not sphere)
            if (initialRotation !== null && initialRotation !== undefined && this.sceneManager) {
                const rotationRadians = (initialRotation * Math.PI) / 180;
                this.sceneManager.setCameraRotation(rotationRadians);
                console.log('%c[ROTATION] Applied on addImage:', 'color: red', initialRotation + '°');
            }

            // Trigger callback
            if (this.onImageAdded) {
                this.onImageAdded(imageData);
            }
        }

            console.log(`✅ Image added${silent ? ' (silent)' : ''}: ${file.name} (Total: ${this.images.length})`);
            return imageData;
        } catch (error) {
            console.error(`[ImageManager] Failed to add image ${file.name}:`, error);
            throw new ImageLoadError(`Could not load image: ${error.message}`, {
                filename: file.name,
                originalError: error
            });
        }
    }

    /**
     * Add a new 360° image with a preloaded texture (no loading lag)
     * @param {File} file - Image file
     * @param {THREE.Texture} texture - Preloaded texture
     * @param {string} url - Image URL
     * @param {number|null} initialRotation - Initial rotation in degrees
     * @param {boolean} silent - If true, don't apply texture or trigger callbacks (for batch loading)
     * @param {string|null} thumbnailUrl - Optional thumbnail URL (WebP format)
     * @returns {Promise<Object>} Image data
     */
    async addImageWithTexture(file, texture, url, initialRotation = null, silent = false, thumbnailUrl = null) {
        // Calculate hash for deduplication
        const hash = await this.calculateFileHash(file);

        // Check if this image already exists
        const existingImageData = this.hashToImageMap.get(hash);
        if (existingImageData) {
            console.warn(`[ImageManager] Image "${file.name}" already exists (duplicate detected). Using existing image (id: ${existingImageData.id}).`);

            // Show toast notification to user
            if (this.toast) {
                this.toast.warning(`L'image "${file.name}" a déjà été ajoutée.`, 3500);
            }

            // Dispose the provided texture since we're using the existing one
            if (texture) {
                texture.dispose();
            }
            return existingImageData;
        }

        this.imageCounter++;
        const imageData = {
            id: this.imageCounter,
            name: file.name,
            url: url,
            texture: texture,
            initialRotation: initialRotation,
            thumbnail_url: thumbnailUrl, // Add thumbnail_url to imageData
            hash: hash
        };

        this.images.push(imageData);
        this.currentImageIndex = this.images.length - 1;

        // Store hash mapping for deduplication
        this.hashToImageMap.set(hash, imageData);

        // Trigger thumbnail creation immediately (before UI callbacks)
        if (this.onThumbnailNeeded) {
            this.onThumbnailNeeded(imageData);
        }

        if (!silent) {
            // Apply texture to sphere
            this.sphereManager.setTexture(texture);

            // Apply initial rotation if specified (via camera, not sphere)
            if (initialRotation !== null && initialRotation !== undefined && this.sceneManager) {
                const rotationRadians = (initialRotation * Math.PI) / 180;
                this.sceneManager.setCameraRotation(rotationRadians);
                console.log('%c[ROTATION] Applied on addImageWithTexture:', 'color: red', initialRotation + '°');
            }

            // Trigger callback
            if (this.onImageAdded) {
                this.onImageAdded(imageData);
            }
        }

        console.log(`✅ Image added with preloaded texture${silent ? ' (silent)' : ''}: ${file.name} (Total: ${this.images.length})`);
        return imageData;
    }

    /**
     * Switch to a specific image by index
     * @param {number} index - Image index
     */
    switchToImage(index) {
        const t0 = performance.now();
        console.log(`[PERF] 🔄 switchToImage(${index}) - START`);

        if (index < 0 || index >= this.images.length) {
            console.warn('Invalid image index:', index);
            return;
        }

        if (index === this.currentImageIndex) {
            console.log(`[PERF] Already on image ${index}, skipping`);
            return; // Already showing this image
        }

        if (this.isTransitioning) {
            console.log(`[PERF] Already transitioning, skipping`);
            return; // Don't start new transition during existing one
        }

        const imageData = this.images[index];
        console.log(`[PERF] Found image: "${imageData.name}", has texture:`, !!imageData.texture);

        const t1 = performance.now();
        console.log(`[PERF] Preparation: ${(t1 - t0).toFixed(2)}ms`);

        this.startTransition(imageData);

        const t2 = performance.now();
        console.log(`[PERF] switchToImage TOTAL: ${(t2 - t0).toFixed(2)}ms`);
    }

    /**
     * Start transition to a new image
     * @param {Object} imageData - Target image data
     */
    startTransition(imageData) {

        if (this.isTransitioning) return;

        this.isTransitioning = true;
        this.transitionStartTime = performance.now();
        this.transitionProgress = 0;

        this.currentTexture = this.sphereManager.sphereMaterial.map;
        this.nextTexture = imageData.texture;

        // Reset camera start position for smooth interpolation
        this.cameraStartPosition = null;

        // Store start and target rotation for smooth interpolation
        // IMPORTANT: Keep undefined as undefined, don't convert to 0
        const currentImage = this.images[this.currentImageIndex];
        this.startRotation = (currentImage && currentImage.initialRotation !== undefined) ? currentImage.initialRotation : undefined;
        this.targetRotation = imageData.initialRotation !== undefined ? imageData.initialRotation : undefined;

        console.log('%c[ROTATION] Transition started', 'color: red; font-weight: bold');
        console.log('%c  Current image:', 'color: red', currentImage?.name, '- initialRotation:', currentImage?.initialRotation);
        console.log('%c  Target image:', 'color: red', imageData.name, '- initialRotation:', imageData.initialRotation);
        console.log('%c  startRotation (computed):', 'color: red', this.startRotation);
        console.log('%c  targetRotation (computed):', 'color: red', this.targetRotation);

        // PRE-APPLY the texture to transition sphere NOW (before it's visible)
        // This forces GPU shader compilation BEFORE the first render frame
        if (this.transitionSphere && this.nextTexture) {
            this.transitionSphere.material.map = this.nextTexture;
            this.transitionSphere.material.needsUpdate = true;
            this.transitionSphere.material.opacity = 0.0;
            this.transitionSphere.visible = false; // Still hidden

            // No rotation applied to sphere - rotation is handled by camera
            this.transitionSphere.rotation.y = -(Math.PI / 2);
        }


        // Trigger callback
        if (this.onTransitionStart) {
            this.onTransitionStart(imageData);
        }
    }

    /**
     * Update transition state (call this in animation loop)
     */
    updateTransition(scene) {
        if (!this.isTransitioning) return;

        const currentTime = performance.now();
        const elapsed = currentTime - this.transitionStartTime;
        this.transitionProgress = Math.min(elapsed / this.transitionDuration, 1);

        // On the first frame, make the sphere visible
        if (this.transitionProgress < 0.1 && !this._firstFrameLogged) {
            const t0 = performance.now();
            console.log(`[PERF] ⚡ First updateTransition frame`);

            // Just make visible - texture is already set and compiled in startTransition
            if (this.transitionSphere) {
                this.transitionSphere.visible = true;
            }

            const t1 = performance.now();
            console.log(`[PERF] Make transition sphere visible: ${(t1 - t0).toFixed(2)}ms`);

            this._firstFrameLogged = true;
        }

        // Smoothly interpolate camera rotation during transition (position changes naturally via rotation)
        if (this.sceneManager && this.sceneManager.controls) {
            const smoothProgress = this.smoothstep(0.0, 1.0, this.transitionProgress);

            // Store start camera position on first frame
            if (!this.cameraStartPosition) {
                this.cameraStartPosition = {
                    x: this.sceneManager.camera.position.x,
                    y: this.sceneManager.camera.position.y,
                    z: this.sceneManager.camera.position.z
                };

                // Calculate start rotation from camera position
                this.cameraStartAngle = Math.atan2(
                    this.cameraStartPosition.x,
                    this.cameraStartPosition.z
                );
            }

            // Calculate target angle from initialRotation (or keep current angle)
            let targetAngle = this.cameraStartAngle;
            if (this.targetRotation !== undefined) {
                targetAngle = (this.targetRotation * Math.PI) / 180;
            }

            // Interpolate angle smoothly
            const interpolatedAngle = this.cameraStartAngle + (targetAngle - this.cameraStartAngle) * smoothProgress;

            // Calculate camera radius (distance from center)
            const radius = Math.sqrt(
                this.cameraStartPosition.x * this.cameraStartPosition.x +
                this.cameraStartPosition.z * this.cameraStartPosition.z
            );

            // Apply interpolated rotation
            this.sceneManager.camera.position.x = radius * Math.sin(interpolatedAngle);
            this.sceneManager.camera.position.y = this.cameraStartPosition.y; // Keep Y unchanged
            this.sceneManager.camera.position.z = radius * Math.cos(interpolatedAngle);

            if (smoothProgress === 0 || smoothProgress === 1) {
                console.log('%c[ROTATION] Interpolating angle:', 'color: red',
                    'progress:', smoothProgress.toFixed(2),
                    'angle:', (interpolatedAngle * 180 / Math.PI).toFixed(1) + '°');
            }

            this.sceneManager.camera.lookAt(0, 0, 0);
            this.sceneManager.controls.update();
        }

        // Update opacity with smoothstep
        if (this.transitionSphere) {
            const smoothProgress = this.smoothstep(0.0, 1.0, this.transitionProgress);
            this.transitionSphere.material.opacity = smoothProgress;
        }

        if (this.transitionProgress >= 1) {
            this.completeTransition(scene);
            this._firstFrameLogged = false; // Reset for next transition
            this.cameraStartPosition = null; // Reset camera position
        }
    }

    /**
     * Complete the transition
     */
    completeTransition(scene) {
        this.isTransitioning = false;
        this.transitionProgress = 0;

        // Update current image index
        this.currentImageIndex = this.images.findIndex(img => img.texture === this.nextTexture);

        const imageData = this.images[this.currentImageIndex];

        // Switch to the new texture on main sphere
        this.sphereManager.setTexture(this.nextTexture);

        // Rotation was already interpolated during transition in updateTransition()
        // No need to apply it again here

        // Hide transition sphere (don't destroy it, reuse it next time)
        if (this.transitionSphere) {
            this.transitionSphere.visible = false;
            this.transitionSphere.material.opacity = 0.0;
        }

        // Trigger callbacks
        if (this.onTransitionComplete) {
            this.onTransitionComplete(this.images[this.currentImageIndex]);
        }

        if (this.onImageChanged) {
            this.onImageChanged(this.currentImageIndex);
        }

        console.log('✅ Transition complete');
    }

    /**
     * Smoothstep function for natural transitions
     */
    smoothstep(edge0, edge1, x) {
        const t = Math.max(0, Math.min(1, (x - edge0) / (edge1 - edge0)));
        return t * t * (3 - 2 * t);
    }

    /**
     * Set transition duration
     * @param {number} duration - Duration in milliseconds
     */
    setTransitionDuration(duration) {
        this.transitionDuration = Math.max(
            TRANSITION_CONFIG.MIN_DURATION,
            Math.min(TRANSITION_CONFIG.MAX_DURATION, duration)
        );
    }

    /**
     * Get current image data
     * @returns {Object|null}
     */
    getCurrentImage() {
        return this.currentImageIndex >= 0 ? this.images[this.currentImageIndex] : null;
    }

    /**
     * Get all images
     * @returns {Array}
     */
    getAllImages() {
        return this.images;
    }

    /**
     * Get image by index
     * @param {number} index - Image index
     * @returns {Object|null}
     */
    getImage(index) {
        return this.images[index] || null;
    }

    /**
     * Check if currently transitioning
     * @returns {boolean}
     */
    isInTransition() {
        return this.isTransitioning;
    }

    /**
     * Store the mapping between picture ID and image data (for API loaded images)
     * @param {string|number} pictureId - Picture ID from API
     * @param {Object} imageData - Image data object
     */
    setPictureIdMapping(pictureId, imageData) {
        this.pictureIdToImageMap.set(pictureId.toString(), imageData);
    }

    /**
     * Get image data by picture ID
     * @param {string|number} pictureId - Picture ID from API
     * @returns {Object|null} Image data or null if not found
     */
    getImageByPictureId(pictureId) {
        return this.pictureIdToImageMap.get(pictureId.toString()) || null;
    }

    /**
     * Remove an image by index
     * @param {number} index - Image index
     */
    removeImage(index) {
        if (index < 0 || index >= this.images.length) {
            console.warn(`[ImageManager] Invalid image index: ${index}`);
            return;
        }

        const imageData = this.images[index];
        console.log(`[ImageManager] Removing image: ${imageData.name}`);

        // Dispose of the texture
        if (imageData.texture) {
            imageData.texture.dispose();
        }

        // Revoke the URL blob if necessary
        if (imageData.url && imageData.url.startsWith('blob:')) {
            URL.revokeObjectURL(imageData.url);
        }

        // Remove from array
        this.images.splice(index, 1);

        // Remove from hash map if present
        if (imageData.hash) {
            this.hashToImageMap.delete(imageData.hash);
        }

        // Remove from picture ID map if present
        for (const [key, value] of this.pictureIdToImageMap.entries()) {
            if (value === imageData) {
                this.pictureIdToImageMap.delete(key);
                break;
            }
        }

        // Adjust current index
        if (this.currentImageIndex >= this.images.length) {
            this.currentImageIndex = Math.max(0, this.images.length - 1);
        }
    }

    /**
     * Cleanup and dispose all resources
     */
    dispose() {
        console.log('[ImageManager] Disposing...');

        // Dispose all images
        this.images.forEach((imageData, index) => {
            if (imageData.texture) {
                imageData.texture.dispose();
            }
            if (imageData.url && imageData.url.startsWith('blob:')) {
                URL.revokeObjectURL(imageData.url);
            }
        });

        this.images = [];
        this.pictureIdToImageMap.clear();
        this.hashToImageMap.clear();

        // Dispose transition sphere
        if (this.transitionSphere) {
            if (this.transitionSphere.geometry) {
                this.transitionSphere.geometry.dispose();
            }
            if (this.transitionSphere.material) {
                this.transitionSphere.material.dispose();
            }
            if (this.transitionSphere.parent) {
                this.transitionSphere.parent.remove(this.transitionSphere);
            }
            this.transitionSphere = null;
        }

        // Clear callbacks
        this.onImageAdded = null;
        this.onImageChanged = null;
        this.onTransitionStart = null;
        this.onTransitionComplete = null;
        this.onThumbnailNeeded = null;

        console.log('[ImageManager] Disposed successfully');
    }
}

